import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { X, Upload, FileSpreadsheet, Download, CheckCircle2, AlertTriangle, Loader2, Trash2, Rocket } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import { loadsApi } from '../lib/api';
import api from '../lib/api';
import { indianCities, truckTypes, materialTypes } from '../mock/mockData';

const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

const templateHeaders = ['origin', 'destination', 'material', 'weight', 'truck_code', 'rate', 'payment_terms', 'notes'];

const validateRow = (row) => {
  const errors = [];
  const origin = String(row.origin || '').trim();
  const destination = String(row.destination || '').trim();
  const rate = parseInt(row.rate || 0);

  if (!origin) errors.push('Origin missing');
  else if (!indianCities.some(c => c.toLowerCase() === origin.toLowerCase())) errors.push('Unknown origin city');

  if (!destination) errors.push('Destination missing');
  else if (!indianCities.some(c => c.toLowerCase() === destination.toLowerCase())) errors.push('Unknown destination city');

  if (!rate || rate < 1000) errors.push('Rate must be >= 1000');
  if (origin && destination && origin.toLowerCase() === destination.toLowerCase()) errors.push('Origin = destination');

  const truck = truckTypes.find(t => t.code === (row.truck_code || '').toString().trim().toUpperCase()) || truckTypes[0];

  return {
    origin: indianCities.find(c => c.toLowerCase() === origin.toLowerCase()) || origin,
    destination: indianCities.find(c => c.toLowerCase() === destination.toLowerCase()) || destination,
    material: String(row.material || 'General').trim(),
    weight: String(row.weight || '10 T').trim(),
    truck_code: truck.code,
    truck_type: truck.label,
    rate,
    distance: 500,
    payment_terms: String(row.payment_terms || 'Advance 30%').trim(),
    notes: String(row.notes || '').trim(),
    _errors: errors,
  };
};

const sampleRows = [
  { origin: 'Mumbai, MH', destination: 'Delhi, DL', material: 'Textiles & Garments', weight: '18 T', truck_code: 'TR-32', rate: 85000, payment_terms: 'Advance 30%', notes: 'Handle with care' },
  { origin: 'Bengaluru, KA', destination: 'Chennai, TN', material: 'Electronics', weight: '8 T', truck_code: 'TR-20', rate: 32000, payment_terms: 'On Delivery (COD)', notes: '' },
  { origin: 'Ahmedabad, GJ', destination: 'Pune, MH', material: 'Cement', weight: '25 T', truck_code: 'TR-TR', rate: 62000, payment_terms: 'Advance 50%', notes: 'Site delivery' },
  { origin: 'Hyderabad, TS', destination: 'Kolkata, WB', material: 'Pharmaceuticals', weight: '6 T', truck_code: 'TR-19', rate: 78000, payment_terms: 'Credit 15 days', notes: 'Temp controlled' },
];

export default function BulkUploadDialog({ open, onClose, onSuccess }) {
  const [rows, setRows] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  if (!open) return null;

  const parseFile = (f) => {
    setFile(f);
    const ext = f.name.split('.').pop().toLowerCase();
    if (ext === 'csv') {
      Papa.parse(f, {
        header: true,
        skipEmptyLines: true,
        complete: (res) => setRows(res.data.map(validateRow)),
        error: () => toast({ title: 'Parse failed', description: 'Could not read CSV file.' }),
      });
    } else if (['xlsx', 'xls'].includes(ext)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json(ws);
          setRows(json.map(validateRow));
        } catch (err) { toast({ title: 'Parse failed', description: 'Could not read Excel file.' }); }
      };
      reader.readAsArrayBuffer(f);
    } else {
      toast({ title: 'Unsupported file', description: 'Please upload a .csv or .xlsx file.' });
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) parseFile(f);
  };

  const downloadTemplate = () => {
    const csv = Papa.unparse({ fields: templateHeaders, data: sampleRows.map(r => templateHeaders.map(h => r[h])) });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'ezload_bulk_template.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast({ title: 'Template downloaded', description: 'Fill in your loads and re-upload.' });
  };

  const loadSample = () => setRows(sampleRows.map(validateRow));

  const validCount = rows.filter(r => r._errors.length === 0).length;
  const errorCount = rows.length - validCount;

  const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));
  const clearAll = () => { setRows([]); setFile(null); };

  const submit = async () => {
    const valid = rows.filter(r => r._errors.length === 0).map(r => {
      const { _errors, ...rest } = r;
      return rest;
    });
    if (valid.length === 0) { toast({ title: 'No valid loads', description: 'Fix errors before uploading.' }); return; }
    setUploading(true);
    try {
      const r = await api.post('/loads/bulk', valid);
      toast({ title: 'Bulk upload complete!', description: `${r.data.created} loads posted successfully.` });
      onSuccess && onSuccess(r.data);
      onClose(); clearAll();
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Please sign in first.' });
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-4xl sm:rounded-2xl max-h-[95vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-600 flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-[#0B2545]">Bulk Upload Loads</h3>
              <p className="text-xs text-gray-500">Post up to 200 loads at once from CSV or Excel</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {rows.length === 0 ? (
            <div className="p-6 space-y-4">
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all ${dragging ? 'border-[#00A651] bg-[#00A651]/5' : 'border-gray-300 hover:border-[#00A651]/60 hover:bg-gray-50'}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-[#00A651]/10 flex items-center justify-center mx-auto mb-4">
                  <Upload className="w-8 h-8 text-[#00A651]" />
                </div>
                <p className="font-bold text-[#0B2545]">Drag & drop your file here</p>
                <p className="text-sm text-gray-500 mt-1">or click to browse — CSV, XLS, XLSX supported</p>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={e => e.target.files[0] && parseFile(e.target.files[0])} />
              </div>

              <div className="flex flex-wrap gap-2 justify-center">
                <Button variant="outline" onClick={downloadTemplate}><Download className="w-4 h-4 mr-2" />Download Template</Button>
                <Button variant="outline" onClick={loadSample}><FileSpreadsheet className="w-4 h-4 mr-2" />Load Sample Data</Button>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-700 mb-2">Required columns:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                  {templateHeaders.map(h => (
                    <span key={h} className="bg-white border border-gray-200 px-2 py-1 rounded font-mono text-gray-700">{h}</span>
                  ))}
                </div>
                <p className="text-[10px] text-gray-500 mt-2">Origin/destination must be full India city name (e.g. "Mumbai, MH"). Truck codes: TR-32, TR-24, TR-20, TR-TR, TR-19, etc.</p>
              </div>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#00A651]/10 text-[#00A651] border-0">{validCount} valid</Badge>
                  {errorCount > 0 && <Badge className="bg-red-50 text-red-600 border-0">{errorCount} with errors</Badge>}
                  <span className="text-xs text-gray-500">{file?.name}</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={clearAll}><Trash2 className="w-3.5 h-3.5 mr-1" />Clear</Button>
                </div>
              </div>

              <div className="overflow-x-auto border border-gray-100 rounded-xl">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600 uppercase text-[10px]">
                    <tr>
                      <th className="text-left px-2 py-2">#</th>
                      <th className="text-left px-2 py-2">Route</th>
                      <th className="text-left px-2 py-2">Material</th>
                      <th className="text-left px-2 py-2">Weight</th>
                      <th className="text-left px-2 py-2">Truck</th>
                      <th className="text-right px-2 py-2">Rate</th>
                      <th className="text-left px-2 py-2">Payment</th>
                      <th className="text-center px-2 py-2">Status</th>
                      <th className="px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className={`border-t border-gray-100 ${r._errors.length > 0 ? 'bg-red-50/50' : ''}`}>
                        <td className="px-2 py-2 font-mono text-gray-400">{i + 1}</td>
                        <td className="px-2 py-2">
                          <p className="font-semibold text-[#0B2545]">{r.origin?.split(',')[0]} → {r.destination?.split(',')[0]}</p>
                        </td>
                        <td className="px-2 py-2 text-gray-600">{r.material}</td>
                        <td className="px-2 py-2 text-gray-600">{r.weight}</td>
                        <td className="px-2 py-2 font-mono text-[10px]">{r.truck_code}</td>
                        <td className="px-2 py-2 text-right font-bold text-[#00A651]">{r.rate ? formatINR(r.rate) : '—'}</td>
                        <td className="px-2 py-2 text-gray-600">{r.payment_terms}</td>
                        <td className="px-2 py-2 text-center">
                          {r._errors.length === 0 ? (
                            <Badge className="bg-[#00A651]/10 text-[#00A651] border-0 text-[10px]"><CheckCircle2 className="w-3 h-3 mr-1" />OK</Badge>
                          ) : (
                            <Badge className="bg-red-50 text-red-600 border-0 text-[10px]" title={r._errors.join(', ')}>
                              <AlertTriangle className="w-3 h-3 mr-1" />{r._errors.length}
                            </Badge>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <button onClick={() => removeRow(i)} className="text-gray-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {errorCount > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800">
                  <p className="font-semibold mb-1">Fix these errors before uploading:</p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {rows.filter(r => r._errors.length > 0).slice(0, 5).map((r, i) => (
                      <li key={i}>Row {rows.indexOf(r) + 1}: {r._errors.join(', ')}</li>
                    ))}
                    {errorCount > 5 && <li>+ {errorCount - 5} more...</li>}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 flex flex-wrap justify-between items-center gap-2">
          <div className="text-xs text-gray-500">
            {rows.length > 0 ? `Ready to post ${validCount} valid loads` : 'Drop a file to preview loads'}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={uploading || validCount === 0} className="bg-[#00A651] hover:bg-[#008a43] text-white">
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : <><Rocket className="w-4 h-4 mr-2" />Post {validCount} Loads</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
